/**
 * MemberProfile — /u/:handle. Rich network-style profile page for a member.
 * Sections: banner + avatar + follow, headline/bio, skills, work history,
 * followers preview, mutual followers, and their recent posts (FeedShell in
 * scope='user' mode).
 *
 * If the current user is viewing their own profile, they can edit the
 * headline/bio/skills/work_history via the inline "Edit profile" flow.
 */
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import ToolSelector from '../components/ToolSelector';
import FeedShell from '../components/network/FeedShell';
import PeopleYouMayKnow from '../components/network/PeopleYouMayKnow';
import PeopleAlsoViewed from '../components/network/PeopleAlsoViewed';
import ProfileViewers from '../components/network/ProfileViewers';
import ProfileBadges from '../components/network/ProfileBadges';import SuggestedForYou from '../components/network/SuggestedForYou';
// FreedrIQ score is intentionally hidden from public profiles — personal diagnostic only.
import CommunityAd from '../components/community/CommunityAd';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  UserPlus, Check, MessageCircle, MapPin, Globe, Briefcase, Sparkles,
  Loader2, Edit3, Users, ExternalLink, Camera, Image as ImageIcon,
  Factory, Award, Package, Beaker, Clock as ClockIcon,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const BUSINESS_TYPES = [
  { value: '',            label: '—' },
  { value: 'hobbyist',    label: 'Hobbyist' },
  { value: 'side_hustle', label: 'Side hustle' },
  { value: 'small_biz',   label: 'Small business' },
  { value: 'commercial',  label: 'Commercial operation' },
];
const BUSINESS_LABEL = Object.fromEntries(BUSINESS_TYPES.map((b) => [b.value, b.label]));

const MemberProfile = () => {
  const { API, user } = useContext(AuthContext);
  const { handle } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  // Auto-open the edit dialog when the URL carries ?edit=1 (used by the
  // "Edit" shortcut on the community left-rail profile card).
  useEffect(() => {
    if (searchParams.get('edit') === '1' && profile?.id === user?.id) {
      setEditing(true);
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, profile?.id, user?.id, setSearchParams]);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/network/profile/${handle}`);
      setProfile(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Profile not found');
      navigate('/community');
    } finally { setLoading(false); }
  }, [API, handle, navigate]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const toggleFollow = async () => {
    if (!profile || busy) return;
    setBusy(true);
    try {
      if (profile.viewer_follows) {
        await axios.delete(`${API}/network/follow/${profile.id}`);
        setProfile((p) => ({ ...p, viewer_follows: false, follower_count: Math.max(0, (p.follower_count || 0) - 1) }));
      } else {
        await axios.post(`${API}/network/follow/${profile.id}`);
        setProfile((p) => ({ ...p, viewer_follows: true, follower_count: (p.follower_count || 0) + 1 }));
      }
    } catch { toast.error('Could not update follow'); }
    finally { setBusy(false); }
  };

  const openDm = async () => {
    try {
      const { data } = await axios.post(`${API}/dm/conversations`, {
        peer_id: profile.id,
        initial_message: `Hi ${profile.name?.split(' ')[0] || 'there'} 👋`,
      });
      navigate(`/messages/${data?.conversation?.id || ''}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not open DM'); }
  };

  const handleConnect = async () => {
    if (!profile || busy) return;
    setBusy(true);
    try {
      const status = profile.viewer_connection_status;
      if (status === 'accepted' || status === 'outgoing' || status === 'incoming') {
        // Remove / cancel / decline
        await axios.delete(`${API}/network/connect/${profile.id}`);
        setProfile((p) => ({ ...p, viewer_connection_status: 'none' }));
        toast.success(status === 'accepted' ? 'Connection removed' : 'Request cancelled');
      } else {
        const { data } = await axios.post(`${API}/network/connect/${profile.id}`, {});
        setProfile((p) => ({
          ...p,
          viewer_connection_status: data.status === 'accepted' ? 'accepted' : 'outgoing',
        }));
        toast.success(data.status === 'accepted' ? 'Connected!' : 'Request sent');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update connection'); }
    finally { setBusy(false); }
  };

  if (loading || !profile) return (
    <div className="min-h-screen bg-background text-foreground">
      <ToolSelector />
      <div className="max-w-4xl mx-auto p-6 text-muted-foreground inline-flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> loading profile…
      </div>
    </div>
  );

  const isSelf = profile.id === user?.id;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="member-profile-page">
      <ToolSelector />
      <div className="max-w-[1200px] mx-auto px-4 py-6">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          {/* Left column */}
          <div className="space-y-4">
            {/* Header */}
            <div className="pro-card overflow-hidden" data-testid="profile-header">
              {profile.banner_url ? (
                <div
                  className="h-28 sm:h-36 bg-cover bg-center"
                  style={{ backgroundImage: `url(${profile.banner_url})` }}
                />
              ) : (
                <div className="h-28 sm:h-36 bg-gradient-to-br from-primary/40 via-primary/20 to-transparent" />
              )}
              <div className="p-5 -mt-14 flex items-start gap-4">
                <div className="h-24 w-24 rounded-2xl bg-primary/20 text-primary border-4 border-card flex items-center justify-center text-2xl font-heading font-extrabold overflow-hidden flex-shrink-0">
                  {profile.avatar_url
                    ? <img src={profile.avatar_url} alt="" className="h-full w-full object-cover" />
                    : (profile.name || '?').slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1 pt-14">
                  <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight truncate" data-testid="profile-name">
                    {profile.name}
                  </h1>
                  {profile.headline && (
                    <p className="text-sm text-muted-foreground mt-0.5" data-testid="profile-headline">{profile.headline}</p>
                  )}
                  <div className="mt-2 flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                    {profile.location && (
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {profile.location}</span>
                    )}
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {profile.follower_count} followers · {profile.following_count} following</span>
                    <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> {profile.posts_count} posts</span>
                    <span className="text-[10px] uppercase tracking-widest font-bold text-primary">@{profile.handle}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 pt-14">
                  {isSelf ? (
                    <Button
                      variant="outline"
                      onClick={() => setEditing(true)}
                      className="rounded-full font-bold"
                      data-testid="profile-edit-btn"
                    >
                      <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit profile
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={handleConnect}
                        disabled={busy}
                        variant={profile.viewer_connection_status === 'accepted' ? 'outline' : 'default'}
                        className="rounded-full font-bold"
                        data-testid="profile-connect-btn"
                      >
                        {(() => {
                          const s = profile.viewer_connection_status;
                          if (s === 'accepted') return <><Check className="w-3.5 h-3.5 mr-1" /> Connected</>;
                          if (s === 'outgoing') return <><ClockIcon className="w-3.5 h-3.5 mr-1" /> Pending</>;
                          if (s === 'incoming') return <><UserPlus className="w-3.5 h-3.5 mr-1" /> Accept</>;
                          return <><UserPlus className="w-3.5 h-3.5 mr-1" /> Connect</>;
                        })()}
                      </Button>
                      <Button
                        onClick={toggleFollow}
                        disabled={busy}
                        variant="outline"
                        size="sm"
                        className="rounded-full font-bold h-9"
                        data-testid="profile-follow-btn"
                      >
                        {profile.viewer_follows
                          ? <><Check className="w-3.5 h-3.5 mr-1" /> Following</>
                          : <>Follow</>}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={openDm}
                        size="sm"
                        className="rounded-full font-bold h-9"
                        data-testid="profile-message-btn"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {profile.mutuals?.length > 0 && !isSelf && (
                <div className="px-5 pb-4 -mt-2 text-xs text-muted-foreground">
                  <span className="font-semibold">Also followed by </span>
                  {profile.mutuals.slice(0, 3).map((m, i) => (
                    <span key={m.id}>
                      <span className="font-semibold text-foreground">{m.name}</span>
                      {i < Math.min(profile.mutuals.length, 3) - 1 ? ', ' : ''}
                    </span>
                  ))}
                  {profile.mutuals.length > 3 && ` +${profile.mutuals.length - 3} more`}
                </div>
              )}
            </div>

            {/* Bio */}
            {profile.bio && (
              <div className="pro-card p-5" data-testid="profile-bio">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">About</div>
                <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{profile.bio}</p>
              </div>
            )}

            {/* Operator stats */}
            {(profile.num_machines != null || profile.years_experience != null ||
              profile.batches_per_month != null || profile.tray_capacity != null ||
              profile.business_type || isSelf) && (
              <div className="pro-card p-5" data-testid="profile-operator-stats">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Operator</div>
                  {isSelf && (profile.num_machines == null && profile.years_experience == null && profile.batches_per_month == null) && (
                    <button
                      type="button"
                      onClick={() => setEditing(true)}
                      className="text-[10px] uppercase tracking-widest font-bold text-primary hover:underline"
                      data-testid="operator-stats-add"
                    >
                      + Add stats
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <StatBox icon={Factory} label="Machines" value={profile.num_machines} />
                  <StatBox icon={Award}   label="Years exp"  value={profile.years_experience} />
                  <StatBox icon={Beaker}  label="Batches/mo" value={profile.batches_per_month} />
                  <StatBox icon={Package} label="Trays cap." value={profile.tray_capacity} />
                </div>
                {profile.business_type && (
                  <div className="mt-3 text-xs">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mr-2">Business</span>
                    <span className="inline-flex items-center rounded-full bg-primary/10 text-primary text-[11px] font-bold px-2.5 py-0.5">
                      {BUSINESS_LABEL[profile.business_type] || profile.business_type}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Equipment — freeze dryers the operator owns */}
            {(profile.equipment?.length > 0 || isSelf) && (
              <div className="pro-card p-5" data-testid="profile-equipment">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                    Equipment {profile.equipment?.length > 0 && `· ${profile.equipment.length}`}
                  </div>
                  {isSelf && (
                    <button
                      type="button"
                      onClick={() => setEditing(true)}
                      className="text-[10px] uppercase tracking-widest font-bold text-primary hover:underline"
                      data-testid="equipment-manage-btn"
                    >
                      {profile.equipment?.length ? 'Manage →' : '+ Add machine'}
                    </button>
                  )}
                </div>
                {profile.equipment?.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {profile.equipment.map((m) => (
                      <EquipmentCard key={m.id || `${m.brand}-${m.model || ''}`} m={m} />
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground italic">
                    Share which freeze dryers you own so peers can ask you real-world questions about them.
                  </div>
                )}
              </div>
            )}

            {/* Course completion badges */}
            <ProfileBadges userId={profile.id} />

            {/* FreedrIQ score is INTENTIONALLY hidden on public profiles —
                it's a personal growth diagnostic, not a public badge. */}

            {/* Skills */}
            {profile.skills?.length > 0 && (
              <div className="pro-card p-5" data-testid="profile-skills">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Skills · click to endorse</div>
                <div className="flex flex-wrap gap-1.5">
                  {(profile.skills_hydrated || profile.skills.map((s) => ({ name: s, count: 0, viewer_has_endorsed: false }))).map((s) => (
                    <SkillPill
                      key={s.name}
                      skill={s}
                      isSelf={isSelf}
                      onEndorsed={(payload) => {
                        setProfile((p) => ({
                          ...p,
                          skills_hydrated: (p.skills_hydrated || p.skills.map((sk) => ({ name: sk, count: 0, viewer_has_endorsed: false }))).map((x) =>
                            x.name === payload.skill ? { ...x, count: payload.count, viewer_has_endorsed: payload.endorsed } : x
                          ),
                        }));
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            <RecommendationsSection
              profile={profile}
              isSelf={isSelf}
              onChanged={fetchProfile}
            />

            {/* Work history */}
            {profile.work_history?.length > 0 && (
              <div className="pro-card p-5" data-testid="profile-work-history">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-3">Experience</div>
                <ol className="space-y-3">
                  {profile.work_history.map((w) => (
                    <li key={w.id || `${w.company}-${w.role}`} className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <Briefcase className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-heading font-extrabold text-sm truncate">{w.role}</div>
                        <div className="text-xs text-muted-foreground truncate">{w.company}{w.location ? ` · ${w.location}` : ''}</div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mt-0.5">
                          {formatMonth(w.start_month)}{w.is_current ? ' — Present' : (w.end_month ? ` — ${formatMonth(w.end_month)}` : '')}
                        </div>
                        {w.description && <div className="text-xs text-foreground/80 mt-1 whitespace-pre-wrap">{w.description}</div>}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Recent activity */}
            <div>
              <h2 className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Recent activity</h2>
              <FeedShell scope="user" authorId={profile.id} showComposer={isSelf} showScopeTabs={false} />
            </div>
          </div>

          {/* Right column */}
          <aside className="space-y-4">
            {profile.website && (
              <div className="pro-card p-4">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Website</div>
                <a
                  href={profile.website}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-sm text-primary inline-flex items-center gap-1 hover:underline"
                >
                  <Globe className="w-3.5 h-3.5" /> {(() => { try { return new URL(profile.website).host; } catch { return profile.website; } })()}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
            {/* Who viewed your profile — self only */}
            {isSelf && <ProfileViewers />}
            {/* People also viewed — for other members' profiles */}
            {!isSelf && <PeopleAlsoViewed userId={profile.id} />}
            <SuggestedForYou compact />
            {/* Sponsored placement — reuses the community ad rotator */}
            <CommunityAd placement="profile" />
            <PeopleYouMayKnow />
            {profile.created_at && (
              <div className="pro-card p-4 text-[11px] text-muted-foreground">
                Joined the community {(() => { try { return format(parseISO(profile.created_at), 'MMMM yyyy'); } catch { return 'a while ago'; } })()}.
              </div>
            )}
          </aside>
        </div>
      </div>

      {editing && <EditProfileDialog profile={profile} onClose={() => setEditing(false)} onSaved={fetchProfile} />}
    </div>
  );
};

const formatMonth = (m) => {
  if (!m) return '';
  try { return format(parseISO(`${m}-01`), 'MMM yyyy'); } catch { return m; }
};

const StatBox = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg border border-border p-2.5 text-center bg-muted/30" data-testid={`operator-stat-${label.replace(/[^a-z]+/gi, '-').toLowerCase()}`}>
    <Icon className="w-4 h-4 text-muted-foreground mx-auto mb-1" />
    <div className="font-heading text-lg font-extrabold text-foreground leading-none">
      {value != null ? value : '—'}
    </div>
    <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground mt-1">
      {label}
    </div>
  </div>
);

const SIZE_LABEL = { small: 'Small', medium: 'Medium', large: 'Large', xl: 'X-Large', custom: 'Custom' };
const COND_LABEL = { new: 'New', used: 'Used', refurb: 'Refurbished' };

const EquipmentCard = ({ m }) => (
  <div className="rounded-lg border border-border p-3 bg-muted/30" data-testid={`equipment-${m.id || m.brand}`}>
    <div className="flex items-center gap-2 mb-1">
      <Factory className="w-3.5 h-3.5 text-primary flex-shrink-0" />
      <div className="font-heading font-extrabold text-sm text-foreground truncate">
        {m.brand}
      </div>
    </div>
    <div className="text-xs text-foreground/85 truncate">
      {m.model || <span className="italic text-muted-foreground">Unspecified model</span>}
    </div>
    <div className="text-[10px] text-muted-foreground mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
      {m.size && <span>{SIZE_LABEL[m.size] || m.size}</span>}
      {m.tray_count ? <span>· {m.tray_count} tray{m.tray_count === 1 ? '' : 's'}</span> : null}
      {m.year_acquired ? <span>· since {m.year_acquired}</span> : null}
      {m.condition && <span>· {COND_LABEL[m.condition] || m.condition}</span>}
    </div>
    {m.notes && (
      <div className="text-[11px] text-foreground/80 italic mt-1 line-clamp-2">{m.notes}</div>
    )}
  </div>
);

const EditProfileDialog = ({ profile, onClose, onSaved }) => {
  const { API } = useContext(AuthContext);
  const [handleV, setHandleV] = useState(profile.handle || '');
  const [headline, setHeadline] = useState(profile.headline || '');
  const [bio, setBio] = useState(profile.bio || '');
  const [website, setWebsite] = useState(profile.website || '');
  const [skillsInput, setSkillsInput] = useState((profile.skills || []).join(', '));
  const [work, setWork] = useState((profile.work_history || []).map((w) => ({ ...w })));
  const [avatarUrl, setAvatarUrl] = useState(profile.avatar_url || '');
  const [bannerUrl, setBannerUrl] = useState(profile.banner_url || '');
  const [numMachines, setNumMachines] = useState(profile.num_machines ?? '');
  const [yearsExp, setYearsExp] = useState(profile.years_experience ?? '');
  const [batchesPerMonth, setBatchesPerMonth] = useState(profile.batches_per_month ?? '');
  const [trayCapacity, setTrayCapacity] = useState(profile.tray_capacity ?? '');
  const [businessType, setBusinessType] = useState(profile.business_type || '');
  const [equip, setEquip] = useState((profile.equipment || []).map((m) => ({ ...m })));
  const [uploading, setUploading] = useState({ avatar: false, banner: false });
  const avatarRef = useRef(null);
  const bannerRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const addWork = () => setWork((w) => [...w, { company: '', role: '', start_month: '', is_current: false, description: '' }]);
  const setWorkAt = (i, patch) => setWork((w) => w.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const rmWork = (i) => setWork((w) => w.filter((_, idx) => idx !== i));

  const addEquip = () => setEquip((e) => [...e, { brand: '', model: '', size: '', tray_count: '', year_acquired: '', condition: '', notes: '' }]);
  const setEquipAt = (i, patch) => setEquip((e) => e.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const rmEquip = (i) => setEquip((e) => e.filter((_, idx) => idx !== i));

  const uploadTo = async (which, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Max 8 MB per image'); return; }
    setUploading((u) => ({ ...u, [which]: true }));
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/uploads/forum`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = data?.url || data?.file_url;
      if (!url) throw new Error('Upload returned no URL');
      if (which === 'avatar') setAvatarUrl(url);
      else setBannerUrl(url);
      toast.success(`${which === 'avatar' ? 'Photo' : 'Banner'} uploaded`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading((u) => ({ ...u, [which]: false })); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const skills = skillsInput.split(',').map((s) => s.trim()).filter(Boolean);
      const work_history = work.filter((w) => w.company && w.role);
      const asInt = (v) => (v === '' || v == null ? null : Math.max(0, parseInt(v, 10) || 0));
      const equipment = equip
        .filter((m) => (m.brand || '').trim())
        .map((m) => ({
          ...m,
          brand:         (m.brand || '').trim(),
          model:         (m.model || '').trim() || null,
          size:          m.size || null,
          condition:     m.condition || null,
          notes:         (m.notes || '').trim() || null,
          tray_count:    asInt(m.tray_count),
          year_acquired: asInt(m.year_acquired),
        }));
      await axios.put(`${API}/network/profile`, {
        handle:           handleV.trim().toLowerCase() || null,
        headline, bio, website, skills, work_history,
        equipment,
        avatar_url:       avatarUrl || null,
        banner_url:       bannerUrl || null,
        num_machines:     asInt(numMachines),
        years_experience: asInt(yearsExp),
        batches_per_month:asInt(batchesPerMonth),
        tray_capacity:    asInt(trayCapacity),
        business_type:    businessType || null,
      });
      toast.success('Profile updated');
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit your profile</DialogTitle>
          <DialogDescription>Fill this out — a rich profile helps other operators find and connect with you.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2" data-testid="profile-edit-form">
          {/* Banner + Avatar */}
          <div className="rounded-lg overflow-hidden border border-border">
            <div
              className="h-24 relative bg-gradient-to-br from-primary/40 via-primary/20 to-transparent bg-cover bg-center"
              style={bannerUrl ? { backgroundImage: `url(${bannerUrl})` } : {}}
            >
              <input
                ref={bannerRef}
                type="file"
                accept="image/*"
                onChange={(e) => uploadTo('banner', e.target.files?.[0])}
                className="hidden"
                data-testid="edit-banner-input"
              />
              <button
                type="button"
                onClick={() => bannerRef.current?.click()}
                disabled={uploading.banner}
                className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-[10px] font-bold px-2 py-1 hover:bg-black/80"
                data-testid="edit-banner-btn"
              >
                {uploading.banner ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                {bannerUrl ? 'Change banner' : 'Add banner'}
              </button>
            </div>
            <div className="p-3 flex items-center gap-3 -mt-8">
              <div className="h-16 w-16 rounded-full ring-4 ring-card bg-primary/20 text-primary flex items-center justify-center overflow-hidden flex-shrink-0">
                {avatarUrl
                  ? <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
                  : <Camera className="w-5 h-5" />}
              </div>
              <div className="flex-1">
                <input
                  ref={avatarRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => uploadTo('avatar', e.target.files?.[0])}
                  className="hidden"
                  data-testid="edit-avatar-input"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => avatarRef.current?.click()}
                  disabled={uploading.avatar}
                  className="rounded-full h-7 text-[11px]"
                  data-testid="edit-avatar-btn"
                >
                  {uploading.avatar ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Camera className="w-3 h-3 mr-1" />}
                  {avatarUrl ? 'Change photo' : 'Upload photo'}
                </Button>
                {avatarUrl && (
                  <button
                    type="button"
                    onClick={() => setAvatarUrl('')}
                    className="ml-2 text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:text-destructive"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Handle (URL)</label>
            <Input value={handleV} onChange={(e) => setHandleV(e.target.value)} placeholder="e.g. jane-smith" data-testid="edit-handle" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Headline</label>
            <Input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="One-liner — e.g. Freeze-dry hobbyist · Ohio" maxLength={140} data-testid="edit-headline" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">About</label>
            <Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={4} maxLength={2000} placeholder="Your story…" data-testid="edit-bio" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Website</label>
            <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" data-testid="edit-website" />
          </div>

          {/* Operator stats */}
          <div className="rounded-lg border border-border p-3 space-y-2" data-testid="edit-operator-stats">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Operator stats · optional</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <div className="text-[10px] font-bold text-muted-foreground mb-0.5">Machines</div>
                <Input type="number" min="0" value={numMachines} onChange={(e) => setNumMachines(e.target.value)} placeholder="0" data-testid="edit-num-machines" />
              </div>
              <div>
                <div className="text-[10px] font-bold text-muted-foreground mb-0.5">Years exp</div>
                <Input type="number" min="0" value={yearsExp} onChange={(e) => setYearsExp(e.target.value)} placeholder="0" data-testid="edit-years-exp" />
              </div>
              <div>
                <div className="text-[10px] font-bold text-muted-foreground mb-0.5">Batches/mo</div>
                <Input type="number" min="0" value={batchesPerMonth} onChange={(e) => setBatchesPerMonth(e.target.value)} placeholder="0" data-testid="edit-batches-per-month" />
              </div>
              <div>
                <div className="text-[10px] font-bold text-muted-foreground mb-0.5">Tray cap.</div>
                <Input type="number" min="0" value={trayCapacity} onChange={(e) => setTrayCapacity(e.target.value)} placeholder="0" data-testid="edit-tray-capacity" />
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold text-muted-foreground mb-0.5">Business type</div>
              <select
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                data-testid="edit-business-type"
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                {BUSINESS_TYPES.map((b) => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Skills (comma-separated)</label>
            <Textarea value={skillsInput} onChange={(e) => setSkillsInput(e.target.value)} rows={2} placeholder="Freeze drying, HACCP, Pricing…" data-testid="edit-skills" />
          </div>

          {/* Equipment editor */}
          <div className="rounded-lg border border-border p-3 space-y-2" data-testid="edit-equipment">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Machines you own · optional</div>
              <Button type="button" size="sm" variant="outline" onClick={addEquip} className="rounded-full h-7 text-[11px]" data-testid="edit-equipment-add">
                + Add machine
              </Button>
            </div>
            {equip.length === 0 ? (
              <div className="text-xs text-muted-foreground italic">
                Add the freeze dryers you own so peers can find you when they have questions about specific brands.
              </div>
            ) : (
              <div className="space-y-2">
                {equip.map((m, i) => (
                  <div key={i} className="rounded-md border border-border p-2.5 space-y-2" data-testid={`edit-equipment-row-${i}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-[10px] font-bold text-muted-foreground mb-0.5">Brand *</div>
                        <Input value={m.brand} onChange={(e) => setEquipAt(i, { brand: e.target.value })} placeholder="Harvest Right" data-testid={`edit-equipment-brand-${i}`} />
                      </div>
                      <div>
                        <div className="text-[10px] font-bold text-muted-foreground mb-0.5">Model</div>
                        <Input value={m.model || ''} onChange={(e) => setEquipAt(i, { model: e.target.value })} placeholder="Medium Pharmaceutical" data-testid={`edit-equipment-model-${i}`} />
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <div className="text-[10px] font-bold text-muted-foreground mb-0.5">Size</div>
                        <select
                          value={m.size || ''}
                          onChange={(e) => setEquipAt(i, { size: e.target.value })}
                          data-testid={`edit-equipment-size-${i}`}
                          className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                        >
                          <option value="">—</option>
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                          <option value="xl">X-Large</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold text-muted-foreground mb-0.5">Trays</div>
                        <Input type="number" min="0" value={m.tray_count ?? ''} onChange={(e) => setEquipAt(i, { tray_count: e.target.value })} placeholder="0" data-testid={`edit-equipment-trays-${i}`} />
                      </div>
                      <div>
                        <div className="text-[10px] font-bold text-muted-foreground mb-0.5">Year</div>
                        <Input type="number" min="1980" max="2100" value={m.year_acquired ?? ''} onChange={(e) => setEquipAt(i, { year_acquired: e.target.value })} placeholder="2024" data-testid={`edit-equipment-year-${i}`} />
                      </div>
                      <div>
                        <div className="text-[10px] font-bold text-muted-foreground mb-0.5">Condition</div>
                        <select
                          value={m.condition || ''}
                          onChange={(e) => setEquipAt(i, { condition: e.target.value })}
                          data-testid={`edit-equipment-condition-${i}`}
                          className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                        >
                          <option value="">—</option>
                          <option value="new">New</option>
                          <option value="used">Used</option>
                          <option value="refurb">Refurbished</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-muted-foreground mb-0.5">Notes</div>
                      <Input value={m.notes || ''} onChange={(e) => setEquipAt(i, { notes: e.target.value })} placeholder="Oil pump, upgraded scroll pump…" data-testid={`edit-equipment-notes-${i}`} />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => rmEquip(i)}
                        className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:text-destructive"
                        data-testid={`edit-equipment-remove-${i}`}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Experience</label>
              <Button size="sm" variant="outline" onClick={addWork} className="rounded-full h-7 text-[11px]" data-testid="edit-add-work">+ Add role</Button>
            </div>
            <div className="space-y-2">
              {work.map((w, i) => (
                <div key={i} className="rounded-lg border border-border p-2 space-y-1.5" data-testid={`edit-work-${i}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={w.role} onChange={(e) => setWorkAt(i, { role: e.target.value })} placeholder="Role" />
                    <Input value={w.company} onChange={(e) => setWorkAt(i, { company: e.target.value })} placeholder="Company" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input value={w.start_month || ''} onChange={(e) => setWorkAt(i, { start_month: e.target.value })} placeholder="YYYY-MM start" />
                    <Input value={w.end_month || ''} onChange={(e) => setWorkAt(i, { end_month: e.target.value })} placeholder="YYYY-MM end" disabled={w.is_current} />
                    <label className="text-xs inline-flex items-center gap-1">
                      <input type="checkbox" checked={!!w.is_current} onChange={(e) => setWorkAt(i, { is_current: e.target.checked, end_month: e.target.checked ? null : w.end_month })} /> Current
                    </label>
                  </div>
                  <Textarea rows={2} value={w.description || ''} onChange={(e) => setWorkAt(i, { description: e.target.value })} placeholder="Short description (optional)" />
                  <div className="flex justify-end">
                    <Button size="sm" variant="ghost" onClick={() => rmWork(i)} className="text-destructive text-xs">Remove</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-full font-bold" data-testid="edit-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MemberProfile;

// -- Sub-components -----------------------------------------------------------

const SkillPill = ({ skill, isSelf, onEndorsed }) => {
  const { API } = useContext(AuthContext);
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (isSelf || busy) return;
    setBusy(true);
    try {
      // Look up target user id from the surrounding profile via API path.
      // Pass target_user_id via a data-attr on the wrapper — simpler: fetch
      // via closure by using the path from window.location which has :handle.
      // Instead we accept the profile id from the parent using a data prop.
      const targetId = window.__memberProfileId__;
      const { data } = await axios.post(`${API}/network/endorse`, {
        target_user_id: targetId,
        skill: skill.name,
      });
      onEndorsed && onEndorsed(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not endorse'); }
    finally { setBusy(false); }
  };
  const active = !!skill.viewer_has_endorsed;
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={isSelf || busy}
      className={`group inline-flex items-center gap-1.5 rounded-full text-xs font-semibold px-3 py-1 border transition-colors ${
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20'
      } ${isSelf ? 'cursor-default' : 'cursor-pointer'}`}
      data-testid={`skill-endorse-${skill.name.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <span>{skill.name}</span>
      {skill.count > 0 && (
        <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-white/25 text-[10px] font-bold">
          {skill.count}
        </span>
      )}
    </button>
  );
};

const RecommendationsSection = ({ profile, isSelf, onChanged }) => {
  const { API, user } = useContext(AuthContext);
  const [writing, setWriting] = useState(false);
  const [body, setBody] = useState('');
  const [relationship, setRelationship] = useState('');
  const [busy, setBusy] = useState(false);

  // Expose the target id to child SkillPills — simplest cross-component wire.
  React.useEffect(() => { window.__memberProfileId__ = profile.id; }, [profile.id]);

  const submit = async () => {
    if (body.trim().length < 20) { toast.error('Write at least 20 characters'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/network/recommendations`, {
        recommendee_id: profile.id,
        body:           body.trim(),
        relationship:   relationship.trim() || null,
      });
      toast.success('Recommendation sent — the recipient will review it');
      setWriting(false); setBody(''); setRelationship('');
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send'); }
    finally { setBusy(false); }
  };

  const updateStatus = async (recId, status) => {
    try {
      await axios.put(`${API}/network/recommendations/${recId}`, { status });
      toast.success(status === 'published' ? 'Recommendation published' : 'Recommendation hidden');
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removeRec = async (recId) => {
    if (!window.confirm('Remove this recommendation?')) return;
    try {
      await axios.delete(`${API}/network/recommendations/${recId}`);
      toast.success('Removed');
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const published = profile.recommendations || [];
  const pending   = profile.pending_recommendations || [];

  if (!isSelf && published.length === 0 && !user) return null;

  return (
    <div className="pro-card p-5 space-y-4" data-testid="profile-recommendations">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          Recommendations ({published.length})
        </div>
        {!isSelf && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setWriting((v) => !v)}
            className="rounded-full h-7 text-[11px] font-bold"
            data-testid="give-recommendation-btn"
          >
            {writing ? 'Cancel' : `Recommend ${profile.name?.split(' ')[0] || 'this member'}`}
          </Button>
        )}
      </div>

      {writing && (
        <div className="rounded-lg border border-border p-3 space-y-2" data-testid="recommendation-form">
          <Input
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            placeholder="How do you know them? (e.g. Fellow learner in FDAcademy 101)"
            maxLength={120}
            data-testid="rec-relationship-input"
          />
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            placeholder="Write a short recommendation (min 20 chars)…"
            maxLength={2000}
            data-testid="rec-body-input"
          />
          <div className="flex justify-end">
            <Button
              onClick={submit}
              disabled={busy}
              className="rounded-full h-8 text-xs font-bold"
              data-testid="rec-submit"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Send for review
            </Button>
          </div>
        </div>
      )}

      {isSelf && pending.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600 mb-2">
            Pending your review ({pending.length})
          </div>
          <ul className="space-y-3">
            {pending.map((r) => (
              <li key={r.id} className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid={`rec-pending-${r.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm">{r.recommender?.name}</span>
                  {r.relationship && <span className="text-[11px] text-muted-foreground">· {r.relationship}</span>}
                </div>
                <p className="text-sm text-foreground whitespace-pre-wrap">{r.body}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Button size="sm" onClick={() => updateStatus(r.id, 'published')} className="rounded-full h-7 text-[11px] font-bold" data-testid={`rec-publish-${r.id}`}>Publish</Button>
                  <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, 'hidden')} className="rounded-full h-7 text-[11px]">Hide</Button>
                  <Button size="sm" variant="ghost" onClick={() => removeRec(r.id)} className="rounded-full h-7 text-[11px] text-destructive">Delete</Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {published.length > 0 ? (
        <ul className="space-y-3">
          {published.map((r) => (
            <li key={r.id} className="rounded-lg border border-border p-3" data-testid={`rec-${r.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-sm">{r.recommender?.name}</span>
                {r.relationship && <span className="text-[11px] text-muted-foreground">· {r.relationship}</span>}
              </div>
              <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{r.body}</p>
              {(isSelf || r.recommender?.id === user?.id) && (
                <div className="flex justify-end mt-2">
                  <button type="button" onClick={() => removeRec(r.id)} className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:text-destructive">
                    Remove
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-xs text-muted-foreground italic">
          {isSelf ? 'No recommendations yet — ask a peer to write one.' : `${profile.name?.split(' ')[0] || 'This member'} has no recommendations yet.`}
        </div>
      )}
    </div>
  );
};
